/**
 * Games Framework Types
 * Core types for the modular games system
 */

// ============================================
// GAME CHALLENGE TYPES
// ============================================

/**
 * Challenge status for versus games
 */
export type GameChallengeStatus = 
  | 'pending' 
  | 'accepted' 
  | 'declined' 
  | 'expired' 
  | 'cancelled';

/**
 * Game challenge for versus games (direct challenges between users)
 */
export interface GameChallenge {
  id: string;
  game_slug: string;
  challenger_id: string;
  challenged_id: string;
  weekend_id: string | null;
  mode: SessionMode;
  status: GameChallengeStatus;
  session_id: string | null;
  game_config: GameChallengeConfig;
  message: string | null;
  expires_at: string;
  responded_at: string | null;
  created_at: string;
  // Joined data
  challenger?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  challenged?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  game?: GameDefinition;
  session?: GameSession;
}

/**
 * Game-specific configuration for challenges
 */
export interface GameChallengeConfig {
  // Chess time controls
  time_control?: 'none' | '5min' | '10min' | '15+10' | '30min';
  show_legal_moves?: boolean;
  allow_undo?: boolean;
  // Memory game
  image_set?: 'family' | 'random';
  grid_size?: '4x3' | '4x4' | '5x4';
  // Generic
  [key: string]: unknown;
}

/**
 * Create challenge parameters
 */
export interface CreateChallengeParams {
  game_slug: string;
  challenged_id: string;
  mode: SessionMode;
  game_config?: GameChallengeConfig;
  message?: string;
}

// ============================================
// RUNNER CHARACTER TYPES
// ============================================

/**
 * Character for endless runner game
 */
export interface RunnerCharacter {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  specialty: string;
  ability_name: string;
  ability_description: string | null;
  sprite_url: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
}

/**
 * Runner character abilities
 */
export type RunnerAbility = 
  | 'double_jump'      // Oma - jumps 50% higher
  | 'break_obstacle'   // Opa - breaks through 1 obstacle
  | 'speed_boost'      // Atleet - 20% faster
  | 'small_hitbox'     // Kind - smaller hitbox
  | 'double_coins'     // Chef - 2x coins
  | 'shield';          // Muzikant - one-time protection

// ============================================
// MEMORY GAME TYPES
// ============================================

/**
 * Image set for memory game
 */
export interface MemoryImageSet {
  id: string;
  name: string;
  description: string | null;
  type: 'family' | 'random' | 'custom';
  images: string[];
  weekend_id: string | null;
  is_active: boolean;
  created_at: string;
}

/**
 * Memory game difficulty
 */
export type MemoryDifficulty = 'easy' | 'medium' | 'hard';

/**
 * Memory game grid configurations
 */
export const MEMORY_GRID_SIZES: Record<MemoryDifficulty, { rows: number; cols: number; pairs: number }> = {
  easy: { rows: 3, cols: 4, pairs: 6 },
  medium: { rows: 4, cols: 4, pairs: 8 },
  hard: { rows: 4, cols: 5, pairs: 10 },
};

// ============================================
// GAME-SPECIFIC STATE TYPES
// ============================================

/**
 * Boter Kaas en Eieren game state
 */
export interface TicTacToeState {
  board: (string | null)[]; // 9 cells, 'X', 'O', or null
  currentTurn: string; // user_id of current player
  player_x: string; // user_id of X player
  player_o: string; // user_id of O player
  winner: string | null;
  is_draw: boolean;
  moves: TicTacToeMove[];
}

export interface TicTacToeMove {
  player: string;
  position: number;
  symbol: 'X' | 'O';
  timestamp: string;
}

/**
 * Chess game state
 */
export interface ChessState {
  fen: string; // Standard chess FEN notation
  moves: string[]; // Algebraic notation moves
  currentTurn: string; // user_id
  player_white: string;
  player_black: string;
  time_white_ms?: number;
  time_black_ms?: number;
  last_move_at?: string;
  draw_offered_by?: string | null;
  resigned_by?: string | null;
  checkmate: boolean;
  stalemate: boolean;
  winner: string | null;
}

/**
 * Memory game state
 */
export interface MemoryGameState {
  cards: MemoryCard[];
  flipped_indices: number[];
  matched_pairs: number[];
  moves: number;
  current_player: string;
  player_scores: Record<string, number>;
  game_completed: boolean;
}

export interface MemoryCard {
  id: string;
  image: string;
  pair_id: string;
}

/**
 * Sudoku game state
 */
export interface SudokuState {
  puzzle: number[][]; // 9x9 grid, 0 = empty
  solution: number[][];
  user_entries: number[][];
  hints_used: number;
  errors: number;
  completed: boolean;
  difficulty: 'easy' | 'medium' | 'hard';
  start_time: string;
}

/**
 * Match 3 game state
 */
export interface Match3State {
  grid: Match3Piece[][];
  score: number;
  level: number;
  moves_remaining?: number;
  time_remaining_ms?: number;
  combo_count: number;
}

export interface Match3Piece {
  type: string;
  id: string;
}

/**
 * Endless Runner game state
 */
export interface EndlessRunnerState {
  character_slug: string;
  distance: number;
  coins: number;
  score: number;
  is_game_over: boolean;
  ability_used: boolean;
  obstacles_avoided: number;
  high_score: number;
}

// ============================================
// ACTIVE GAMES & LEADERBOARD TYPES
// ============================================

/**
 * Active game for spectator view
 */
export interface ActiveGame {
  session: GameSession;
  game: GameDefinition;
  participants: {
    user_id: string;
    full_name: string;
    avatar_url: string | null;
  }[];
  started_ago_seconds: number;
  is_waiting: boolean;
}

/**
 * Free play leaderboard entry
 */
export interface FreePlayLeaderboardEntry {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  team_id: string | null;
  team_name: string | null;
  team_color: string | null;
  games_played: number;
  games_won: number;
  win_rate: number;
  total_points: number;
  best_score: number;
  current_streak: number;
}

/**
 * Leaderboard scope/filter
 */
export type LeaderboardScope = 'all_time' | 'weekend' | 'monthly' | 'weekly';

// ============================================
// ORIGINAL TYPES (existing)
// ============================================

/**
 * Game definition from the database registry
 */
export interface GameDefinition {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  category: string;
  min_players: number;
  max_players: number | null;
  supports_weekend: boolean;
  supports_free_play: boolean;
  supports_teams: boolean;
  supports_solo: boolean;
  default_points_config: PointsConfig;
  default_config: Record<string, unknown>;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Points configuration for a game
 */
export interface PointsConfig {
  win?: number;
  participation?: number;
  bonus?: number;
  correct_answer?: number;
  bonus_speed?: number;
  item_found?: number;
  all_items_bonus?: number;
  first_team_bonus?: number;
  [key: string]: number | undefined;
}

/**
 * Weekend game configuration
 */
export interface WeekendGameConfig {
  id: string;
  weekend_id: string;
  game_slug: string;
  is_enabled: boolean;
  use_custom_dates: boolean;
  starts_at: string | null;
  ends_at: string | null;
  points_config: PointsConfig;
  custom_config: Record<string, unknown>;
  order_index: number;
  created_at: string;
  updated_at: string;
  // Joined data
  game?: GameDefinition;
}

/**
 * Game session participant
 */
export interface SessionParticipant {
  user_id: string;
  team_id?: string;
  display_name: string;
  avatar_url?: string;
  role: "host" | "player" | "spectator";
  score: number;
  joined_at: string;
}

/**
 * Game session status
 */
export type SessionStatus =
  | "waiting"
  | "active"
  | "completed"
  | "cancelled"
  | "expired";

/**
 * Game session mode
 */
export type SessionMode = "weekend" | "free" | "practice";

/**
 * Game session type
 */
export type SessionType = "solo" | "versus" | "team" | "coop";

/**
 * Game session from database
 */
export interface GameSession {
  id: string;
  game_slug: string;
  weekend_id: string | null;
  mode: SessionMode;
  session_type: SessionType;
  status: SessionStatus;
  created_by: string;
  participants: SessionParticipant[];
  game_state: Record<string, unknown>;
  result: GameResult | null;
  winner_user_id: string | null;
  winner_team_id: string | null;
  points_awarded: number;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  game?: GameDefinition;
  creator?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

/**
 * Game result data
 */
export interface GameResult {
  winner_user_id?: string;
  winner_team_id?: string;
  final_scores: Record<string, number>;
  stats: Record<string, unknown>;
  duration_ms?: number;
}

/**
 * User game statistics
 */
export interface UserGameStats {
  id: string;
  user_id: string;
  game_slug: string;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  total_points_earned: number;
  personal_best_score: number;
  personal_best_time_ms: number | null;
  current_streak: number;
  longest_streak: number;
  stats: Record<string, unknown>;
  achievements: GameAchievement[];
  last_played_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  game?: GameDefinition;
  user?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

/**
 * Game achievement/badge
 */
export interface GameAchievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  earned_at: string;
}

/**
 * Leaderboard entry
 */
export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  game_slug: string;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  total_points: number;
  win_rate: number;
  current_streak: number;
  longest_streak: number;
}

// CompetitionSettings is defined in @/types/index.ts
export type { CompetitionSettings } from "@/types";

/**
 * Game with weekend config (combined data for display)
 */
export interface GameWithConfig extends GameDefinition {
  weekend_config?: WeekendGameConfig;
  is_available_now: boolean;
  is_weekend_mode: boolean;
  effective_points_config: PointsConfig;
}

/**
 * Create session parameters
 */
export interface CreateSessionParams {
  game_slug: string;
  mode: SessionMode;
  session_type: SessionType;
  weekend_id?: string;
  quiz_id?: string;
  participants?: Omit<SessionParticipant, "joined_at">[];
  initial_state?: Record<string, unknown>;
}

/**
 * Update session parameters
 */
export interface UpdateSessionParams {
  status?: SessionStatus;
  game_state?: Record<string, unknown>;
  result?: GameResult;
  winner_user_id?: string;
  winner_team_id?: string;
  points_awarded?: number;
  add_participant?: Omit<SessionParticipant, "joined_at">;
  remove_participant_id?: string;
}

/**
 * Game module configuration (for registering new games)
 */
export interface GameModuleConfig {
  slug: string;
  definition: Omit<GameDefinition, "id" | "created_at" | "updated_at">;
  routes: {
    lobby: string;
    play: string;
    admin?: string;
  };
}

